import { useEffect, useState } from "react";
import { ShieldCheck, Users } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { PageHero, Reveal, EmptyState } from "@/components/public/Shared";

export default function ChurchFamily() {
  const [families, setFamilies] = useState(null);

  useEffect(() => {
    api.get("/public/families").then((r) => setFamilies(r.data)).catch(() => setFamilies([]));
  }, []);

  return (
    <div data-testid="church-family-page">
      <PageHero
        title="Our Church Family"
        subtitle="One family in Christ — growing, serving and worshipping together in Rajpura."
        image="https://images.pexels.com/photos/5638601/pexels-photo-5638601.jpeg?auto=compress&cs=tinysrgb&w=1800"
      />
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
        <Reveal>
          <div className="mb-12 flex items-start gap-4 rounded-lg border border-gold/40 bg-gold/10 p-6" data-testid="family-privacy-note">
            <ShieldCheck className="h-6 w-6 shrink-0 text-gold-dark" />
            <div>
              <p className="font-semibold text-navy">Family Privacy</p>
              <p className="mt-1 text-sm text-foreground/75">
                Our full church family directory is private and protected. Only families who have explicitly
                chosen to be listed publicly appear on this page, and only with minimal information.
                Personal contact details, addresses and dates of birth are never published.
              </p>
            </div>
          </div>
        </Reveal>

        {families && families.length === 0 && (
          <EmptyState
            testId="families-empty"
            title="No public family profiles yet"
            text="Families who choose to share their profiles publicly will appear here. The complete directory is available only to authorised church administrators."
          />
        )}

        <div className="grid gap-8 md:grid-cols-2">
          {(families || []).map((f, i) => (
            <Reveal key={f.family_id} delay={i * 0.08}>
              <article className="h-full rounded-lg border border-border bg-white p-8 shadow-sm" data-testid={`public-family-${f.family_id}`}>
                <div className="flex items-center gap-4">
                  {f.photo_url ? (
                    <img src={fileUrl(f.photo_url)} alt={`${f.family_name} family`} className="h-16 w-16 rounded-full object-cover ring-2 ring-gold/40" loading="lazy" />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary text-navy/40">
                      <Users className="h-7 w-7" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-heading text-xl font-bold text-navy">{f.family_name}</h3>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Family {f.family_id}</p>
                  </div>
                </div>
                {f.members.length > 0 && (
                  <ul className="mt-6 space-y-3">
                    {f.members.map((m, j) => (
                      <li key={j} className="flex items-center gap-3 text-sm">
                        {m.photo_url ? (
                          <img src={fileUrl(m.photo_url)} alt={m.full_name} className="h-9 w-9 rounded-full object-cover" loading="lazy" />
                        ) : (
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary font-heading text-xs font-bold text-navy/60">
                            {m.full_name?.charAt(0) || "?"}
                          </span>
                        )}
                        <span className="font-semibold text-navy">{m.full_name}</span>
                        {m.relationship && <span className="text-muted-foreground">· {m.relationship}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
